import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Card,
  CardContent,
  Link,
} from '@mui/material'
import { Security } from '@mui/icons-material'
import { authApi } from '@api/endpoints/auth'
import { useAuthStore } from '@store/authStore'

export default function MfaVerifyPage() {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [useBackupCode, setUseBackupCode] = useState(false)
  const { mfaToken, login, mfaRequired } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    // Redirect if no MFA token
    if (!mfaRequired || !mfaToken) {
      navigate('/login')
    }
  }, [mfaRequired, mfaToken, navigate])

  const handleCodeChange = (value: string) => {
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6)
    setCode(numericValue)

    // Auto-submit when 6 digits entered
    if (numericValue.length === 6 && !useBackupCode) {
      handleSubmit(numericValue)
    }
  }

  const handleSubmit = async (codeValue?: string) => {
    const verificationCode = codeValue || code

    if (!verificationCode || !mfaToken) {
      setError('Please enter a verification code')
      return
    }

    setError(null)
    setIsLoading(true)

    try {
      const response = await authApi.verifyMfa({
        mfaToken,
        code: verificationCode,
      })

      if (
        response.success &&
        response.token &&
        response.refreshToken &&
        response.user &&
        response.expiresAt &&
        response.refreshTokenExpiresAt
      ) {
        // MFA verification successful
        login(
          response.token,
          response.refreshToken,
          response.user,
          response.expiresAt,
          response.refreshTokenExpiresAt,
        )
        navigate('/', { replace: true })
      } else {
        setError(response.message || 'Verification failed')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {useBackupCode
                ? 'Enter a backup code from your recovery codes'
                : 'Enter the 6-digit code from your authenticator app'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <TextField
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              label={useBackupCode ? 'Backup Code' : 'Verification Code'}
              fullWidth
              margin="normal"
              autoFocus
              inputProps={{
                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' },
                maxLength: useBackupCode ? 12 : 6,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              disabled={isLoading}
            />

            {!useBackupCode && (
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || code.length !== 6}
                sx={{ mt: 2, mb: 2 }}
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </Button>
            )}

            {useBackupCode && (
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || code.length === 0}
                sx={{ mt: 2, mb: 2 }}
              >
                {isLoading ? 'Verifying...' : 'Verify Backup Code'}
              </Button>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setUseBackupCode(!useBackupCode)
                  setCode('')
                  setError(null)
                }}
                sx={{ cursor: 'pointer' }}
              >
                {useBackupCode ? 'Use authenticator code instead' : 'Use backup code instead'}
              </Link>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/login')}
                sx={{ cursor: 'pointer' }}
              >
                Back to login
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
